import React, { useState, useEffect } from 'react';
import { LiveKitRoom, AudioConference, useDataChannel, useRoomContext, useParticipants, useIsSpeaking } from '@livekit/components-react';
import '@livekit/components-styles';
import AuthWrapper from './components/AuthWrapper';
import AuthService from './services/AuthService';
import EvaluationResults from './components/EvaluationResults';
import './App.css';

const WS_URL = process.env.REACT_APP_LIVEKIT_SERVER_URL || "wss://agent-mentor.penmen.net";
const PERSONAS_API_URL = "https://xvtl66dwye.execute-api.us-east-1.amazonaws.com/prod/personas";

console.log("🔧 Frontend Version: 2025-11-03-18:20 - Dynamic Personas:", WS_URL);

function SpeakingIndicators({ personaName }) {
  const participants = useParticipants();
  const room = useRoomContext();
  
  const localParticipant = participants.find(p => p.isLocal);
  const remoteParticipant = participants.find(p => !p.isLocal);

  return (
    <div className="session-controls-header">
      <div className="speaking-indicators">
        {localParticipant && <ParticipantSpeaker participant={localParticipant} label="You" />}
        {remoteParticipant && <ParticipantSpeaker participant={remoteParticipant} label={personaName} />}
      </div>
      <button onClick={() => room.disconnect()} className="leave-call-btn">
        End Session
      </button>
    </div>
  );
}

function ParticipantSpeaker({ participant, label }) {
  const isSpeaking = useIsSpeaking(participant);
  return (
    <div className={`speaker-badge ${isSpeaking ? 'speaking' : ''}`}>
      <span className="speaker-dot"></span>
      <span className="speaker-label">{label}</span>
    </div>
  );
}

function ActiveTranscriptPanel({ personaName, selectedPersona, messages, setMessages, sessionStart }) {
  const [currentTime, setCurrentTime] = useState(Date.now());
  
  // Update timer every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const formatDuration = (ms) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('en-US', { 
      hour12: false, 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit'
    });
  };
  
  useDataChannel((message) => {
    try {
      const data = JSON.parse(new TextDecoder().decode(message.payload));
      if (data.type === 'transcript') {
        setMessages(prev => [...prev, { 
          speaker: data.speaker, 
          text: data.text, 
          timestamp: Date.now() 
        }]);
      }
    } catch (e) {
      console.error('Failed to parse transcript data:', e);
    }
  });

  return (
    <div className="transcript-panel">
      <div className="session-header">
        <div className="session-info">
          <h2>Training with {personaName}</h2>
          <div className="persona-quick-info">
            <span className="persona-detail">{selectedPersona?.age} years old</span>
            <span className="persona-detail">{selectedPersona?.occupation}</span>
            <span className="persona-detail">Lead Score: {selectedPersona?.lead_score}/100</span>
          </div>
        </div>
        <div className="session-timer">
          <div className="timer-label">Session Time</div>
          <div className="timer-value">{formatDuration(currentTime - sessionStart)}</div>
        </div>
      </div>
      
      <div className="transcript-header">
        <h3>Live Transcript</h3>
        <div className="message-count">{messages.length} messages</div>
      </div>
      
      <div className="messages">
        {messages.map((msg, idx) => (
          <div key={idx} className={`message ${msg.speaker}`}>
            <div className="message-header">
              <span className="speaker-name">
                {msg.speaker === 'user' ? 'Trainee' : personaName}
              </span>
              <span className="message-time">{formatTime(msg.timestamp)}</span>
            </div>
            <div className="message-text">{msg.text}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ReviewTranscriptPanel({ personaName, selectedPersona, messages, sessionDuration, onNewSession, onEndSession, showEvaluateButton, onEvaluate }) {
  console.log('[ReviewTranscriptPanel] Rendering with showEvaluateButton:', showEvaluateButton);
  const formatDuration = (ms) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('en-US', { 
      hour12: false, 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <div className="transcript-panel">
      <div className="session-header">
        <div className="session-info">
          <h2>Training with {personaName}</h2>
          <div className="persona-quick-info">
            <span className="persona-detail">{selectedPersona?.age} years old</span>
            <span className="persona-detail">{selectedPersona?.occupation}</span>
            <span className="persona-detail">Lead Score: {selectedPersona?.lead_score}/100</span>
          </div>
        </div>
        <div className="session-timer">
          <div className="timer-label">Final Time</div>
          <div className="timer-value">{formatDuration(sessionDuration)}</div>
        </div>
      </div>
      
      <div className="transcript-header">
        <h3>Session Complete - Review Transcript</h3>
        <div className="message-count">{messages.length} messages</div>
      </div>
      
      <div className="messages">
        {messages.map((msg, idx) => (
          <div key={idx} className={`message ${msg.speaker}`}>
            <div className="message-header">
              <span className="speaker-name">
                {msg.speaker === 'user' ? 'Trainee' : personaName}
              </span>
              <span className="message-time">{formatTime(msg.timestamp)}</span>
            </div>
            <div className="message-text">{msg.text}</div>
          </div>
        ))}
      </div>

      <div className="session-controls">
        <div className="session-summary">
          <h4>Session Summary</h4>
          <p>Duration: {formatDuration(sessionDuration)}</p>
          <p>Messages exchanged: {messages.length}</p>
        </div>
        <div className="control-buttons">
          {showEvaluateButton ? (
            <>
              <button onClick={onEvaluate} className="evaluate-btn">
                📊 Evaluate Session
              </button>
              <button onClick={onEndSession} className="end-session-btn">
                🏠 Back to Dashboard
              </button>
            </>
          ) : (
            <>
              <button onClick={onNewSession} className="new-session-btn">
                🔄 Start New Session
              </button>
              <button onClick={onEndSession} className="end-session-btn">
                🏠 Back to Dashboard
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function App({ user, onLogout }) {
  const [token, setToken] = useState('');
  const [connected, setConnected] = useState(false);
  const [callEnded, setCallEnded] = useState(false);
  const [messages, setMessages] = useState([]);
  const [sessionStart, setSessionStart] = useState(null);
  const [personas, setPersonas] = useState([]);
  const [selectedPersona, setSelectedPersona] = useState(null);
  const [trainingMode, setTrainingMode] = useState('training'); // 'training' or 'evaluation'
  const [loading, setLoading] = useState(true);
  const [evaluationLoading, setEvaluationLoading] = useState(false);
  const [evaluationResults, setEvaluationResults] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [showEvaluateButton, setShowEvaluateButton] = useState(false);

  useEffect(() => {
    fetchPersonas();
  }, []);

  const fetchPersonas = async () => {
    try {
      const response = await fetch(PERSONAS_API_URL);
      const data = await response.json();
      setPersonas(data.personas);
      // Default to Sarah Johnson
      const sarah = data.personas.find(p => p.persona_id === 'sarah_johnson');
      setSelectedPersona(sarah || data.personas[0]);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch personas:', error);
      setLoading(false);
    }
  };

  const generateToken = async () => {
    // Get current user for room name
    const user = AuthService.getCurrentUser();
    const userId = user?.userId || 'anonymous';
    
    // Call token API via ALB with HTTPS (LiveKit Cloud)
    const response = await fetch('https://agent-mentor.penmen.net/api/token', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        room: `insurance-training-${selectedPersona?.persona_id || 'default'}-${userId}-${trainingMode}`,
        identity: 'trainee'
      })
    });
    const data = await response.json();
    return data.token;
  };

  const handleConnect = async () => {
    try {
      const newToken = await generateToken();
      const user = AuthService.getCurrentUser();
      const userId = user?.userId || 'anonymous';
      const generatedSessionId = `session-${userId}-${selectedPersona?.persona_id || 'default'}`;
      
      setToken(newToken);
      setSessionId(generatedSessionId);
      setConnected(true);
      setCallEnded(false);
      setMessages([]);
      setSessionStart(Date.now());
      setEvaluationResults(null);
    } catch (error) {
      console.error('Failed to get token:', error);
    }
  };

  const handleDisconnect = async () => {
    console.log('[handleDisconnect] Called');
    console.log('[handleDisconnect] trainingMode:', trainingMode);
    console.log('[handleDisconnect] sessionId:', sessionId);
    
    setConnected(false);
    setCallEnded(true);
    
    // If evaluation mode, show evaluate button instead of auto-evaluating
    if (trainingMode === 'evaluation' && sessionId) {
      console.log('[handleDisconnect] Setting showEvaluateButton to true');
      setShowEvaluateButton(true);
    } else {
      console.log('[handleDisconnect] Not evaluation mode or no sessionId');
    }
  };

  const handleEndSession = () => {
    setConnected(false);
    setCallEnded(false);
    setMessages([]);
    setSessionStart(null);
    setToken('');
  };

  const handleNewSession = async () => {
    try {
      const newToken = await generateToken();
      const user = AuthService.getCurrentUser();
      const userId = user?.userId || 'anonymous';
      const generatedSessionId = `session-${userId}-${selectedPersona?.persona_id || 'default'}`;
      
      setToken(newToken);
      setSessionId(generatedSessionId);
      setConnected(true);
      setCallEnded(false);
      setMessages([]);
      setSessionStart(Date.now());
      setEvaluationResults(null);
      setShowEvaluateButton(false);
    } catch (error) {
      console.error('Failed to get token:', error);
    }
  };

  const handleEvaluateClick = async () => {
    console.log('[handleEvaluateClick] Called');
    console.log('[handleEvaluateClick] sessionId:', sessionId);
    
    setShowEvaluateButton(false);
    setEvaluationLoading(true);
    
    try {
      const token = AuthService.getIdToken();
      console.log('[handleEvaluateClick] Token obtained:', token ? 'Yes' : 'No');
      
      console.log('[handleEvaluateClick] Calling evaluation API...');
      const response = await fetch(`https://agent-mentor.penmen.net/coaching/evaluate/${sessionId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('[handleEvaluateClick] Response status:', response.status);
      
      if (response.ok) {
        const evaluation = await response.json();
        console.log('[handleEvaluateClick] Evaluation received:', evaluation);
        setEvaluationResults(evaluation);
      } else {
        console.error('[handleEvaluateClick] Evaluation failed:', response.status);
        const errorText = await response.text();
        console.error('[handleEvaluateClick] Error details:', errorText);
      }
    } catch (error) {
      console.error('[handleEvaluateClick] Exception:', error);
    } finally {
      console.log('[handleEvaluateClick] Setting evaluationLoading to false');
      setEvaluationLoading(false);
    }
  };

  if (loading) {
    return (
      <div className='livekit'>
        <div className='title'>AI Sales Training - Practice with Virtual Prospects</div>
        <div>Loading personas...</div>
      </div>
    );
  }

  if (!connected && !callEnded) {
    return (
      <div className='livekit'>
        <div className='header'>
          <div className='title-section'>
            <div className='title'>AI Sales Training - Practice with Virtual Prospects</div>
            <div className='subtitle'>Practice your sales pitch with AI-powered prospects and get real-time coaching</div>
          </div>
          <div className="user-header">
            <span className="user-welcome">Welcome, {user?.name || user?.email}</span>
            <button onClick={onLogout} className="logout-btn">Logout</button>
          </div>
        </div>
        
        <div className='persona-selector-bar'>
          <label htmlFor="persona-select">Choose Your Prospect:</label>
          <select 
            id="persona-select"
            value={selectedPersona?.persona_id || ''}
            onChange={(e) => {
              const persona = personas.find(p => p.persona_id === e.target.value);
              setSelectedPersona(persona);
            }}
          >
            {personas.map(persona => (
              <option key={persona.persona_id} value={persona.persona_id}>
                {persona.name}
              </option>
            ))}
          </select>
        </div>

        {/* Training Mode Selector */}
        <div className='mode-selector-bar'>
          <div className='mode-selector'>
            <button 
              className={`mode-btn ${trainingMode === 'training' ? 'active' : ''}`}
              onClick={() => setTrainingMode('training')}
            >
              <span className='mode-icon'>🎓</span>
              <span className='mode-title'>Training Mode</span>
              <span className='mode-desc'>Practice with AI coaching hints</span>
            </button>
            <button 
              className={`mode-btn ${trainingMode === 'evaluation' ? 'active' : ''}`}
              onClick={() => setTrainingMode('evaluation')}
            >
              <span className='mode-icon'>📊</span>
              <span className='mode-title'>Evaluation Mode</span>
              <span className='mode-desc'>Test your skills without hints</span>
            </button>
          </div>
        </div>

        {selectedPersona && (
          <div className='dashboard'>
            <div className='dashboard-section persona-details'>
              <h3>👤 Prospect Profile</h3>
              <div className='persona-stats'>
                <div className='stat-item'>
                  <span className='stat-label'>Name:</span>
                  <span className='stat-value'>{selectedPersona.name}</span>
                </div>
                <div className='stat-item'>
                  <span className='stat-label'>Age:</span>
                  <span className='stat-value'>{selectedPersona.age} years old</span>
                </div>
                <div className='stat-item'>
                  <span className='stat-label'>Occupation:</span>
                  <span className='stat-value'>{selectedPersona.occupation}</span>
                </div>
                <div className='stat-item'>
                  <span className='stat-label'>Status:</span>
                  <span className='stat-value'>{selectedPersona.marital_status}</span>
                </div>
                <div className='stat-item'>
                  <span className='stat-label'>Lead Score:</span>
                  <span className='stat-value score'>{selectedPersona.lead_score}/100</span>
                </div>
              </div>
            </div>

            <div className='dashboard-section quick-tips'>
              <h3>💡 Key Insights</h3>
              <div className='tip-item'>
                <strong>Primary Concern:</strong>
                <p>{selectedPersona.primary_concern}</p>
              </div>
              <div className='tip-item'>
                <strong>Coverage Interest:</strong>
                <p>{selectedPersona.coverage_interest}</p>
              </div>
              <div className='tip-item'>
                <strong>Lead Source:</strong>
                <p>{selectedPersona.campaign_source}</p>
              </div>
            </div>

            <div className='dashboard-section training-tools'>
              <h3>🛠️ Training Resources</h3>
              <div className='tool-buttons'>
                <button 
                  onClick={() => {
                    const personaId = selectedPersona?.persona_id || 'default';
                    window.open(`/ai-coaching?persona=${encodeURIComponent(personaId)}&mode=${trainingMode}`, '_blank', 'width=1326,height=1559,left=1250,top=0');
                  }}
                  className="tool-btn scripts-btn"
                  disabled={trainingMode === 'evaluation'}
                  style={{ opacity: trainingMode === 'evaluation' ? 0.5 : 1, cursor: trainingMode === 'evaluation' ? 'not-allowed' : 'pointer' }}
                >
                  🎓 AI Coaching
                  <span className='btn-desc'>
                    {trainingMode === 'evaluation' ? 'Not available in Evaluation mode' : 'Real-time hints & conversation guidance'}
                  </span>
                </button>
                <button 
                  onClick={() => window.open('/products', '_blank', 'width=900,height=700')}
                  className="tool-btn products-btn"
                  disabled={trainingMode === 'evaluation'}
                  style={{ opacity: trainingMode === 'evaluation' ? 0.5 : 1, cursor: trainingMode === 'evaluation' ? 'not-allowed' : 'pointer' }}
                >
                  📊 Product Details
                  <span className='btn-desc'>
                    {trainingMode === 'evaluation' ? 'Not available in Evaluation mode' : 'Insurance products & benefits'}
                  </span>
                </button>
              </div>
            </div>
          </div>
        )}
        
        <div className='start-session-area'>
          <button onClick={handleConnect} className={`start-btn ${trainingMode === 'evaluation' ? 'evaluation-mode' : ''}`} disabled={!selectedPersona}>
            {trainingMode === 'training' ? '🎯 Start Training Session' : '📊 Start Evaluation Session'}
          </button>
          <p className='start-hint'>
            {trainingMode === 'training' 
              ? 'Open training resources in separate windows for best experience'
              : 'Test your skills independently - evaluation report will be generated after the session'}
          </p>
        </div>
      </div>
    );
  }

  // Connected state - active call
  if (connected) {
    return (
      <div className='livekit'>
        <div className="session-container">
          <LiveKitRoom 
            audio={true} 
            video={false} 
            token={token} 
            serverUrl={WS_URL} 
            connect={true}
            onDisconnected={handleDisconnect}
            data-lk-theme="default"
          >
            <div className="audio-section">
              <AudioConference />
            </div>
            <SpeakingIndicators personaName={selectedPersona?.name || 'Agent'} />
            <ActiveTranscriptPanel 
              personaName={selectedPersona?.name || 'Agent'} 
              selectedPersona={selectedPersona}
              messages={messages}
              setMessages={setMessages}
              sessionStart={sessionStart}
            />
          </LiveKitRoom>
        </div>
      </div>
    );
  }

  // Call ended state - review transcript or show evaluation
  if (callEnded) {
    const sessionDuration = sessionStart ? Date.now() - sessionStart : 0;
    
    // Show evaluation loading state
    if (trainingMode === 'evaluation' && evaluationLoading) {
      return (
        <div className='livekit'>
          <div className="session-container">
            <div className="evaluation-loading">
              <div className="loading-spinner"></div>
              <h2>Generating Your Evaluation Report...</h2>
              <p>Analyzing your conversation and performance</p>
            </div>
          </div>
        </div>
      );
    }
    
    // Show evaluation results
    if (trainingMode === 'evaluation' && evaluationResults) {
      return (
        <div className='livekit'>
          <div className="session-container">
            <EvaluationResults 
              evaluation={evaluationResults}
              onNewSession={handleNewSession}
              onEndSession={handleEndSession}
            />
          </div>
        </div>
      );
    }
    
    // Show transcript review (training mode or evaluation failed)
    return (
      <div className='livekit'>
        <div className="session-container">
          <ReviewTranscriptPanel 
            personaName={selectedPersona?.name || 'Agent'} 
            selectedPersona={selectedPersona}
            messages={messages}
            sessionDuration={sessionDuration}
            onNewSession={handleNewSession}
            onEndSession={handleEndSession}
            showEvaluateButton={showEvaluateButton}
            onEvaluate={handleEvaluateClick}
          />
        </div>
      </div>
    );
  }
}

export default function AppWithAuth() {
  return (
    <AuthWrapper>
      <App />
    </AuthWrapper>
  );
}
